import { useCallback, useEffect, useState } from 'react';
import { Phone, Video, MessageSquare, X, CalendarClock, Users, BellOff, BellRing, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import RescheduleButton from './RescheduleButton';
import { ScheduleCallDialog } from './ScheduleDialog';
import { useChat } from '../../context/ChatContext';
import { Pencil, PhoneCall } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { voice: Phone, video: Video, chat: MessageSquare };

const pad = (n) => String(Math.abs(n)).padStart(2, '0');
// hh:mm:ss; negative once the scheduled time has passed
export const fmtCountdown = (ms) => {
  const neg = ms < 0;
  const s = Math.floor(Math.abs(ms) / 1000);
  return `${neg ? '-' : ''}${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

// Floating countdown bubble (over the BBC logo) for the next scheduled call; tap → details card.
export default function ScheduledCallBubble() {
  const { user, token } = useAuth();
  const { initiateCall } = useChat();
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/calls/scheduled`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setItems(await r.json());
    } catch (_) { /* offline */ }
  }, [token]);

  useEffect(() => { load(); const t = setInterval(load, 60000); window.addEventListener('scheduled-call-changed', load); return () => { clearInterval(t); window.removeEventListener('scheduled-call-changed', load); }; }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  if (!user || !items.length) return null;
  const next = items[0]; // earliest (overdue first)
  const diff = new Date(next.scheduled_at).getTime() - now;
  const overdue = diff < 0;
  const Icon = ICONS[next.call_type] || Phone;
  const mine = next.created_by === user.user_id;

  const dismiss = async () => {
    try {
      const r = await fetch(`${API}/api/calls/scheduled/${next.schedule_id}/dismiss`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      toast.success('Countdown disabled'); setOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={next.title || 'Scheduled call'}
        className={`fixed top-1.5 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-1.5 pl-2 pr-2.5 h-7 rounded-full shadow-lg border text-white font-mono text-xs tabular-nums transition-colors ${overdue ? 'bg-red-600 border-red-300/40 animate-pulse' : 'bg-forest-700 border-white/20'}`}
        data-testid="scheduled-call-bubble">
        <Icon className="w-3.5 h-3.5" />
        <span data-testid="scheduled-call-countdown">{fmtCountdown(diff)}</span>
        {items.length > 1 && <span className="text-[10px] opacity-80">+{items.length - 1}</span>}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-12 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-96 z-[71] rounded-2xl bg-background border shadow-2xl p-4 space-y-3" data-testid="scheduled-call-card">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Scheduled {next.call_type === 'chat' ? 'chat' : `${next.call_type} call`}</p>
              <p className="font-semibold truncate" data-testid="scheduled-call-title">{next.title || next.conversation_name || 'Untitled'}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 -mr-1 -mt-1" onClick={() => setOpen(false)} data-testid="scheduled-call-close"><X className="w-4 h-4" /></Button>
          </div>
          <div className={`rounded-xl p-3 text-center font-mono text-3xl tabular-nums ${overdue ? 'bg-red-50 text-red-700' : 'bg-forest-50 text-forest-800'}`} data-testid="scheduled-call-card-countdown">
            {fmtCountdown(diff)}
            <p className="text-[11px] font-sans text-muted-foreground mt-1">{overdue ? 'past the scheduled time' : 'until the call'} · {new Date(next.scheduled_at).toLocaleString([], { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <div className="text-sm space-y-1.5">
            <p className="flex items-center gap-1 text-xs" data-testid="scheduled-call-mode">
              {next.auto_ring ? <><BellRing className="w-3.5 h-3.5 text-forest-700" /><span>Rings everyone automatically at the time</span></> : <><Timer className="w-3.5 h-3.5 text-red-600" /><span>Countdown only — runs negative until disabled</span></>}
            </p>
            <p><span className="text-muted-foreground">Scheduled by:</span> {next.created_by_name || '—'}{mine ? ' (you)' : ''}</p>
            <p className="flex items-start gap-1"><Users className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" /><span className="text-muted-foreground">Participants:</span>&nbsp;<span data-testid="scheduled-call-participants">{(next.participant_names || []).join(', ') || `${(next.participant_ids || []).length} people`}</span></p>
            {next.note && <p className="text-muted-foreground italic">“{next.note}”</p>}
            {items.length > 1 && <p className="text-xs text-muted-foreground">{items.length - 1} more scheduled after this one.</p>}
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              {next.call_type !== 'chat' && next.conversation_id && (
                <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => { setOpen(false); initiateCall(next.conversation_id, next.call_type === 'video' ? 'video' : 'voice'); }} data-testid="scheduled-call-now">
                  <PhoneCall className="w-4 h-4 mr-1.5" /> Call now
                </Button>
              )}
              {mine && (
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { setOpen(false); setEditing(next); }} data-testid="scheduled-call-edit">
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
              )}
            </div>
            {mine && <div className="[&>button]:w-full"><RescheduleButton schedule={next} onDone={load} /></div>}
            <div className="flex gap-2">
              {mine && (
                <Button variant="outline" size="sm" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={dismiss} data-testid="scheduled-call-dismiss">
                  <BellOff className="w-4 h-4 mr-1.5" /> Disable countdown
                </Button>
              )}
              <Button size="sm" className="flex-1" onClick={() => setOpen(false)} data-testid="scheduled-call-close-btn">Close</Button>
            </div>
          </div>
        </div>
      )}
      <ScheduleCallDialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }} editSchedule={editing} onScheduled={load} />
    </>
  );
}
